import Core 					               from '@/core/Core';
import BaseEntity                    from '@/core/misc/BaseEntity';
import ReceiptPosition               from '@/domains/Receipts/ReceiptPosition';
import Shipment                      from '@/domains/Shipments/Shipment';
import DateSimple                    from '@/core/types/DateSimple';

import type ReceiptComplaintPosition from '@/domains/Receipts/ReceiptComplaintPosition';
import type OrderPosition            from '@/domains/Orders/OrderPosition';

import Title_ru_RU                   from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class             from "@/core/misc/decorators/Title_ru_RU_Class";

import TYPES                         from '@/core/services/Changelogs/types';

/**
 * плоский список претензий приёмки (для второго грида в карточке).
 * в списке претензии позиций приемки (ReceiptComplaintPosition — qty, type) + полный orderPosition.
 * почему orderPosition отдельно: внутри претензии он лежит как { id }, без name —
 * поэтому имя товара берём с уровня ReceiptPosition, а не из самой претензии.
 */

export type ReceiptComplaintRow = {
  complaint:     ReceiptComplaintPosition;
  orderPosition: OrderPosition | null;
};

/**
 * Приемка товаров.
 * Фиксирует фактически принятое количество по конкретной отгрузке (Shipment).
 * Соотношение с отгрузкой — 1:1.
 */

@Title_ru_RU_Class('Приемка')
class Receipt extends BaseEntity {

  static Events = {
    Created: 'receipt:created',
    Updated: 'receipt:updated',
    Deleted: 'receipt:deleted',
  } as const;

  /** Дата приемки */
  @Title_ru_RU('Дата приемки', TYPES.DATE)
  public receiptDate: DateSimple;

  /** Отгрузка: либо полный гидрированный Shipment (из get), либо только {id} (из реестра) */
  public shipment: Shipment | { id: number };

  /** Позиции приемки */
  public positions: ReceiptPosition[] = [];

  /** № Oracle — внешний идентификатор приемки из учётной системы */
  public externalId: number | null;

  /** указывает на наличие претензий у приемки,
   * доступен в тонких версиях (как в реестре), где нет позиций */
  public hasComplaints: boolean | null;

  /** Закрыт */
  public isClosed: boolean | null;

  /** геттер для вывода баджика "претензия" на момент создания */
  get hasAnyComplaint(): boolean {
    return this.positions.some(position => position.hasComplaint);
  }

  /**
   * Есть ли хотя бы у одной позиции приёмки ненулевая претензия данного типа.
   * Управляет видимостью колонки-претензии в гриде карточки: колонка
   * показывается, только если тип встречается хотя бы раз. «Недостача»
   * (shortage) выводится всегда и через этот геттер не проверяется.
   */
  hasAnyComplaintOfType(typeId: string): boolean {
    return this.positions.some(position => position.getComplaintQty(typeId).isPositive());
  }

  /**
   * Плоский список претензий по всем позициям приёмки.
   * orderPosition берётся с уровня ReceiptPosition,
   * потому что у самого complaint бэк отдаёт только { id } без name.
   */
  get complaintRows(): ReceiptComplaintRow[] {
    const rows: ReceiptComplaintRow[] = [];

    for (const position of this.positions) {
      for (const complaint of position.complaints) {
        rows.push({ complaint, orderPosition: position.orderPosition });
      }
    }

    return rows;
  }

  /* ────────────────────────────────────────
     Фабричные методы
     ──────────────────────────────────────── */

  /**
   * Черновик с нуля. Предзаполнять нечего — продавца и отгрузку выберет
   * оператор в реквизитах. Фабрика существует, чтобы карточки создавали
   * документы единым протоколом, а не голым конструктором.
   */
  static fromNew(result = new Receipt()): Receipt {
    return result;
  }

  /**
   * Создаёт черновик приёмки на основе отгрузки.
   * Приёмка 1:1 с отгрузкой: позиции строятся из позиций отгрузки,
   * принято по умолчанию равно отгруженному. id не задаётся —
   * новая приёмка сохраняется через add. Дату берём с отгрузки;
   * если её нет — оператор укажет в реквизитах.
   */

  static fromShipment(shipment: Shipment): Receipt {
    const receipt = new Receipt();

    receipt.shipment = shipment;
    receipt.receiptDate = shipment.receiptDate;
    receipt.positions = shipment.positions.map(
      position => ReceiptPosition.fromShipmentPosition(position),
    );

    return receipt;
  }

  /* ────────────────────────────────────────
     fromServer
     ──────────────────────────────────────── */

  static fromServer(data: any, receipt: Receipt = new Receipt()): Receipt {
    super.fromServer(data, receipt);

    receipt.receiptDate   = Core.utils.hydrate(data.receiptDate, DateSimple.fromServer)!;
    receipt.shipment      = Shipment.fromServer(data.shipment);

    receipt.positions = Core.utils.hydrateArray(data.positions, ReceiptPosition.fromServer);

    return receipt;
  }

  /**
   * Причина, по которой уведомление отправить нельзя. null — можно.
   * Приёмки из автоимпорта (externalId заполнен) бэк отклоняет,
   * поэтому пункт меню на них дизейблим.
   */

  static getReasonCannotSendNotification(receipt: Receipt): string | null {
    if (receipt.externalId === null) {
      return null;
    }
    else {
      return 'Уведомление можно отправить только по ручной приёмке';
    }
  }
}

export default Receipt;
